import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Input,
  Spinner,
} from "@nextui-org/react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  decryptSymmetricString,
  encryptSymmetricString,
} from "../crypto/utils";
import { useUserInfo } from "../hooks/useUserInfo";
import { useNavigate, useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import {
  setDraftTitle as reduxSetDraftTitle,
  setOutOfSync,
  setTitleChecksum,
} from "../redux/draftStore";
import { RootState } from "../redux/store";
import { DraftMetadata as gqlDraftMetadata, RoomPresets } from "../gql/graphql";
import {
  Document,
  DocumentDownload,
  Eye,
  EyeSlash,
  Home3,
  Trash,
  Printer,
} from "iconsax-react";
import DraftSelectModal from "./DraftSelectModal";
import { getDraftPath } from "../utils/urlPaths";
import { Socket } from "socket.io-client";
import ProfileMenu from "./ProfileMenu";
import { asBlob } from "html-docx-js-typescript";
import fileDownload from "js-file-download";
import TurndownService from "turndown";
import FeedbackButton from "./FeedbackButton";
import { useMediaQuery } from "react-responsive";
import { Menu } from "lucide-react";

const UPDATE_DRAFT_TITLE_MUTATION = gql`
  mutation updateEncryptedDraftTitle(
    $draftID: String!
    $encryptedTitle: String!
    $oldTitleChecksum: String
  ) {
    updateTitle(
      request: {
        draftID: $draftID
        encryptedTitle: $encryptedTitle
        oldTitleChecksum: $oldTitleChecksum
      }
    ) {
      newTitleChecksum
    }
  }
`;

enum IconDropdownKeys {
  Home = "Home",
  CreateNew = "CreateNew",
  SelectDraft = "SelectDraft",
  DeleteRoom = "DeleteRoom",
  ExportToDocx = "ExportToDocx",
  ExportToMarkdown = "ExportToMarkdown",
  GeneratePrompt = "GeneratePrompt",
  PrintDocument = "PrintDocument",
}

const GET_DRAFTS_METADATA = gql`
  query GetDraftsMetadataDocumentEditor($excludeDraftID: String!) {
    getDraftsMetadata(request: { excludeDraftID: $excludeDraftID }) {
      drafts {
        draftID
        encryptedTitle
        updatedAt
      }
      hasMore
    }
  }
`;

const CREATE_DRAFT = gql`
  mutation CreateDraft {
    createDraft
  }
`;

const GET_ROOM_DATA = gql`
  query getRoomDataDocumentEditorHeader($roomID: String!) {
    getRoomData(request: { roomID: $roomID }) {
      roomID
      prompt
      timeLeftSeconds
      name
      ownerID
      presetID
      promptHidden
      circleID
    }
  }
`;

const UPDATE_ROOM_PROMPT_HIDDEN = gql`
  mutation UpdateRoomPromptHiddenLogoMenu(
    $roomID: String!
    $promptHidden: Boolean!
  ) {
    updateRoomPromptHidden(
      request: { roomID: $roomID, promptHidden: $promptHidden }
    ) {
      success
    }
  }
`;

const DELETE_ROOM = gql`
  mutation DeleteRoom($roomID: String!) {
    deleteRoom(request: { roomID: $roomID }) {
      success
    }
  }
`;

type DraftMetadata = {
  draftID: string;
  title: string | null;
};

function DocumentEditorHeader({ socket }: { socket: Socket | null }) {
  const { userID, decryptionKey } = useUserInfo();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: "(max-width: 680px)",
  });

  const { draftID, roomID } = useParams();

  const [createDraft] = useMutation(CREATE_DRAFT);
  const [deleteRoom] = useMutation(DELETE_ROOM);
  const [lastDraftID, setLastDraftID] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [promptHidden, setPromptHidden] = useState(false);
  const [exportingToWord, setExportingToWord] = useState(false);
  const [exportingToMarkdown, setExportingToMarkdown] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [circleID, setCircleID] = useState<string | null>(null);
  useEffect(() => {
    if (!lastDraftID && draftID) {
      setLastDraftID(draftID);
    }
  }, [draftID, lastDraftID]);

  // if user is navigating between drafts, blow everything away
  useEffect(() => {
    if (draftID && lastDraftID && draftID !== lastDraftID) {
      navigate(0); // equivalent to window.location.reload()
    }
  }, [draftID, lastDraftID, navigate]);
  const { data, loading } = useQuery(GET_DRAFTS_METADATA, {
    variables: {
      excludeDraftID: draftID,
    },
    fetchPolicy: "no-cache",
  });

  const { data: roomData, refetch: refetchRoomData } = useQuery(GET_ROOM_DATA, {
    variables: { roomID },
    skip: !roomID,
    onCompleted: (data) => {
      setRoomName(data.getRoomData.name);
      setPromptHidden(data.getRoomData.promptHidden);
      setCircleID(data.getRoomData.circleID);
    },
    fetchPolicy: "no-cache",
  });

  const [updateRoomPromptHidden] = useMutation(UPDATE_ROOM_PROMPT_HIDDEN);

  const isPresetRoom = useMemo(() => {
    return !!roomData?.getRoomData?.presetID;
  }, [roomData?.getRoomData?.presetID]);
  const draftMetadataList = useMemo<DraftMetadata[]>(() => {
    if (!data || !decryptionKey) {
      return [];
    }
    return data.getDraftsMetadata.drafts.map((metadata: gqlDraftMetadata) => {
      return {
        draftID: metadata.draftID,
        title: decryptSymmetricString({
          encryptedPayload: metadata.encryptedTitle,
          secretKey: decryptionKey,
        }),
      };
    });
  }, [data, decryptionKey]);
  const savedDraftTitle = useSelector((state: RootState) => state.draft.title);
  const titleChecksum = useSelector(
    (state: RootState) => state.draft.titleChecksum
  );
  const [updateEncryptedDraftTitle] = useMutation(UPDATE_DRAFT_TITLE_MUTATION, {
    onCompleted: (data) => {
      if (data.updateTitle) {
        dispatch(setTitleChecksum(data.updateTitle.newTitleChecksum));
      }
    },
    onError: (e) => {
      const errorCodes = e.graphQLErrors.map((error) => error.extensions?.code);
      if (errorCodes.includes("CHECKSUM_MISMATCH")) {
        dispatch(setOutOfSync());
      }
    },
  });
  const [draftTitle, setDraftTitle] = useState("");
  const [allDraftsModalOpen, setAllDraftsModalOpen] = useState(false);
  const draftHtml = useSelector((state: RootState) => state.draft.draftHtml);
  const dispatch = useDispatch();

  const encryptAndUpdateDraftTitle = useCallback(async () => {
    if (!decryptionKey) {
      return;
    }
    const encryptedTitle = encryptSymmetricString({
      secretKey: decryptionKey,
      decryptedPayload: draftTitle,
    });
    await updateEncryptedDraftTitle({
      variables: {
        draftID,
        encryptedTitle,
        oldTitleChecksum: titleChecksum,
      },
    });
  }, [
    decryptionKey,
    draftID,
    draftTitle,
    titleChecksum,
    updateEncryptedDraftTitle,
  ]);

  useEffect(() => {
    if (savedDraftTitle && savedDraftTitle !== draftTitle) {
      setDraftTitle(savedDraftTitle);
    }
  }, [draftTitle, savedDraftTitle]);

  const debouncedEncryptAndUpdateDraftTitle = useDebouncedCallback(
    encryptAndUpdateDraftTitle,
    300
  );

  const isRoomOwner = useMemo(() => {
    return userID === roomData?.getRoomData?.ownerID;
  }, [userID, roomData?.getRoomData?.ownerID]);

  const isPersonalRoom = useMemo(() => {
    return roomData?.getRoomData?.presetID === RoomPresets.PersonalRoom;
  }, [roomData?.getRoomData?.presetID]);

  const showDeleteRoomButton = useMemo(() => {
    return isRoomOwner && !isPersonalRoom;
  }, [isRoomOwner, isPersonalRoom]);

  const handlePrint = useCallback(() => {
    if (!draftHtml || !draftTitle) return;

    // Create a new window for printing
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    // Create print-optimized HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${draftTitle}</title>
          <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&display=swap" rel="stylesheet">
          <style>
            @page {
              margin: 2cm;
              size: auto;
              marks: none;
              margin-top: 2cm !important;
              margin-bottom: 2cm !important;
              margin-left: 2cm !important;
              margin-right: 2cm !important;
            }
            body {
              font-family: 'Lora', serif;
              line-height: 1.6;
              margin: 0;
              padding: 0;
              max-width: 100%;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @media print {
              @page {
                margin: 2cm !important;
              }
              body {
                margin: 0;
                padding: 0;
              }
              /* More aggressive header/footer removal */
              header, footer, .header, .footer {
                display: none !important;
                margin: 0 !important;
                padding: 0 !important;
                height: 0 !important;
                visibility: hidden !important;
              }
              /* Force remove any potential browser-added elements */
              *[data-header], *[data-footer] {
                display: none !important;
              }
            }
            h1 {
              font-family: 'Space Grotesk', sans-serif;
              font-weight: 500;
              margin-bottom: 2em;
            }
            /* Container for content to ensure proper margins */
            .content-wrapper {
              max-width: 100%;
              margin: 0 auto;
              padding: 0;
            }
          </style>
        </head>
        <body>
          <div class="content-wrapper">
            <h1>${draftTitle}</h1>
            ${draftHtml}
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for both the document and fonts to load before printing
    printWindow.onload = () => {
      // Give extra time for the fonts to load
      setTimeout(() => {
        printWindow.print();
        // Close the window after printing (or if user cancels)
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 500);
    };
  }, [draftHtml, draftTitle]);

  return (
    <>
      <DraftSelectModal
        open={allDraftsModalOpen}
        setOpen={setAllDraftsModalOpen}
        dismissable={true}
        roomID={roomID}
      />
      <div
        className="w-full flex flex-row py-2 items-center border-b border-solid border-black px-3 fixed"
        style={{ backgroundColor: "white", zIndex: 1000 }}
      >
        <Dropdown
          className="font-sans"
          closeOnSelect={false}
          isOpen={dropdownOpen}
          onOpenChange={async (isOpen) => {
            if (isOpen) {
              const result = await refetchRoomData();
              setPromptHidden(result.data.getRoomData.promptHidden);
            }
            setDropdownOpen(isOpen);
          }}
        >
          <DropdownTrigger
            className="cursor-pointer"
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
            }}
          >
            <Menu size={24} />
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Special actions"
            disabledKeys={["no-drafts"]}
            onAction={async (key) => {
              let closeDropdown = true;
              if (key === IconDropdownKeys.Home) {
                if (circleID) {
                  navigate(`/circle/${circleID}`);
                } else {
                  navigate("/");
                }
                return;
              } else if (key === IconDropdownKeys.CreateNew) {
                const response: { data?: { createDraft: string } } =
                  await createDraft();
                if (!response.data) {
                  console.error("Failed to create draft");
                  return;
                }
                navigate(
                  getDraftPath({ draftID: response.data.createDraft, roomID })
                );
                return;
              } else if (key === IconDropdownKeys.SelectDraft) {
                setAllDraftsModalOpen(true);
              } else if (key === IconDropdownKeys.DeleteRoom) {
                if (
                  window.confirm("Are you sure you want to delete this room?")
                ) {
                  await deleteRoom({ variables: { roomID } });
                  navigate("/");
                }
              } else if (key === IconDropdownKeys.ExportToDocx) {
                if (!draftHtml) {
                  return;
                }
                setExportingToWord(true);
                try {
                  const buffer = await asBlob(draftHtml);
                  fileDownload(
                    buffer,
                    `${draftTitle ? draftTitle : "Untitled"}.docx`
                  );
                  closeDropdown = true;
                } catch (err) {
                  console.error("Error exporting to Word", { err });
                } finally {
                  setExportingToWord(false);
                }
              } else if (key === IconDropdownKeys.ExportToMarkdown) {
                if (!draftHtml) {
                  return;
                }
                try {
                  const turndownService = new TurndownService();
                  const markdown = turndownService.turndown(draftHtml);
                  fileDownload(
                    markdown,
                    `${draftTitle ? draftTitle : "Untitled"}.md`
                  );
                  closeDropdown = true;
                } catch (err) {
                  console.error("Error exporting to Markdown", { err });
                } finally {
                  setExportingToMarkdown(false);
                }
              } else if (key === IconDropdownKeys.GeneratePrompt) {
                await updateRoomPromptHidden({
                  variables: { roomID, promptHidden: !promptHidden },
                });
                setPromptHidden(!promptHidden);
                navigate(0);
              } else if (key === IconDropdownKeys.PrintDocument) {
                handlePrint();
              } else {
                navigate(getDraftPath({ draftID: key as string, roomID }));
              }
              if (closeDropdown) {
                setDropdownOpen(false);
              }
            }}
          >
            <DropdownSection aria-label="Special actions" showDivider>
              {[
                <DropdownItem
                  key={IconDropdownKeys.Home}
                  startContent={<Home3 size={16} />}
                >
                  {circleID ? "Circle home" : "Home"}
                </DropdownItem>,
                <DropdownItem
                  key={IconDropdownKeys.CreateNew}
                  startContent={<Document size={16} />}
                >
                  New draft
                </DropdownItem>,

                ...(isMobile
                  ? []
                  : [
                      <DropdownItem
                        key={IconDropdownKeys.ExportToDocx}
                        startContent={
                          exportingToWord ? (
                            <Spinner
                              color="default"
                              classNames={{ wrapper: "w-4 h-4" }}
                            />
                          ) : (
                            <DocumentDownload size={16} />
                          )
                        }
                      >
                        Export to Word
                      </DropdownItem>,

                      <DropdownItem
                        key={IconDropdownKeys.ExportToMarkdown}
                        startContent={
                          exportingToMarkdown ? (
                            <Spinner
                              color="default"
                              classNames={{ wrapper: "w-4 h-4" }}
                            />
                          ) : (
                            <DocumentDownload size={16} />
                          )
                        }
                      >
                        Export to Google Docs
                      </DropdownItem>,
                      <DropdownItem
                        key={IconDropdownKeys.PrintDocument}
                        startContent={<Printer size={16} />}
                        onPress={handlePrint}
                      >
                        Print document
                      </DropdownItem>,
                    ]),
              ]}
            </DropdownSection>

            <DropdownSection
              title="Recent drafts"
              showDivider={
                data?.getDraftsMetadata?.hasMore || showDeleteRoomButton
              }
            >
              {loading ? (
                <DropdownItem key="loading-drafts">
                  <div className="flex flex-col items-center">
                    <p className="font-sans mb-2">Loading your drafts...</p>
                    <Spinner />
                  </div>
                </DropdownItem>
              ) : draftMetadataList.length > 0 ? (
                draftMetadataList.map((metadata) => {
                  return (
                    <DropdownItem
                      key={metadata.draftID}
                      className="draft-title-item"
                      title={metadata.title ?? "Untitled"}
                      style={{ maxWidth: 200 }}
                    >
                      {metadata.title ?? "Untitled"}
                    </DropdownItem>
                  );
                })
              ) : (
                <DropdownItem key="no-drafts">No other drafts</DropdownItem>
              )}
            </DropdownSection>
            {data?.getDraftsMetadata?.hasMore && (
              <DropdownSection
                aria-label="Room actions"
                showDivider={showDeleteRoomButton}
              >
                <DropdownItem key={IconDropdownKeys.SelectDraft}>
                  View all drafts
                </DropdownItem>
              </DropdownSection>
            )}
            {showDeleteRoomButton ? (
              <DropdownSection aria-label="Room actions">
                <DropdownItem
                  key={IconDropdownKeys.DeleteRoom}
                  startContent={<Trash size={16} />}
                  className="text-danger"
                >
                  Delete room
                </DropdownItem>
              </DropdownSection>
            ) : null}
          </DropdownMenu>
        </Dropdown>
        <div className="flex-1" style={{ paddingLeft: 12 }}>
          <div className="flex flex-row items-center">
            <div className="flex-1">
              <Input
                placeholder="Document Title"
                classNames={{
                  input: ["font-sans font-semibold text-lg"],
                  inputWrapper: [
                    "bg-transparent",
                    "shadow-none",
                    "data-[hover=true]:bg-transparent",
                    "group-data-[focus=true]:bg-transparent",
                    "h-[48px]",
                    "py-[5px]",
                    "w-full",
                    "px-0",
                    "min-h-unit-0",
                  ],
                  innerWrapper: ["pb-0"],
                }}
                value={draftTitle}
                onValueChange={(value) => {
                  setDraftTitle(value);
                  dispatch(reduxSetDraftTitle({ title: value }));
                  debouncedEncryptAndUpdateDraftTitle();
                }}
              />
            </div>
          </div>
        </div>
        {!isMobile && (
          <div style={{ marginRight: 20 }}>
            <FeedbackButton />
          </div>
        )}
        <ProfileMenu />
      </div>
      <div style={{ minHeight: 57 }}></div>
    </>
  );
}

export default DocumentEditorHeader;
